/**
 * CUSTOMER KNOWLEDGE BASE — the single source of truth for business facts.
 *
 * Everything in this file is drawn from information that already exists on the
 * website. Nothing here is invented. When the business has not published an
 * answer, the answer directs the customer to call rather than guessing.
 *
 * This file feeds:
 *   - the homepage FAQ section (subset flagged `onHomepage`)
 *   - the /faq customer information page (everything)
 *   - the FAQPage structured data on both, generated from the same strings so
 *     schema and visible text can never disagree
 *   - AI chat assistants (e.g. Tidio Lyro) that crawl /faq as a knowledge source
 *
 * RULE: one fact, one wording. If a policy changes, change it here only.
 */
export interface Faq {
  question: string;
  answer: string;
  /** Included in the shorter homepage FAQ list. */
  onHomepage?: boolean;
}
export interface FaqCategory {
  id: string;
  title: string;
  /** Plain-language summary of the category, useful for AI extraction. */
  intro: string;
  faqs: Faq[];
}
/* ------------------------------------------------------------------ */
/* CANONICAL BUSINESS FACTS                                            */
/* ------------------------------------------------------------------ */
export const BUSINESS_FACTS = [{
  label: 'Business name',
  value: "Mike's Pro Detail"
}, {
  label: 'What we do',
  value: 'Mobile car detailing — we come to you'
}, {
  label: 'Phone (call or text)',
  value: '(818) 381-9905'
}, {
  label: 'Website',
  value: 'https://www.fulldetailpro.com'
}, {
  label: 'Service areas',
  value: 'Ventura County, California and select surrounding areas'
}, {
  label: 'Where we work',
  value: 'Your home, office, or apartment — anywhere the vehicle can be safely accessed'
}, {
  label: 'What we need from you',
  value: 'We bring our own water and detailing equipment. We require access to a standard electrical outlet at the service location.'
}, {
  label: 'Storefront',
  value: 'None. We are a fully mobile service, so there is no shop to drop your vehicle off at.'
}, {
  label: 'Price range',
  value: '$90 to $350, depending on the package and vehicle size'
}, {
  label: 'Payment accepted',
  value: 'Cash, credit cards, and most digital payment methods'
}, {
  label: 'Booking',
  value: 'Call or text (818) 381-9905, or send the quote form on the website'
}];
export const PRICING_TABLE = [{
  name: 'Basic Wash',
  smMid: '$90',
  suvCross: '$105',
  note: '2 car minimum for basic washes only',
  includes: ['Exterior Wash', 'Light Wipe Down', 'Vacuum', 'Clean Rims', 'Dress Tires', 'Clean Windows']
}, {
  name: 'Wash & Wax',
  smMid: '$160',
  suvCross: '$180',
  note: 'Cleans your car and adds a protective layer that helps paint stay shiny and repel water and dirt',
  includes: ['Exterior Wash', 'Window Cleaning', 'Vacuum', 'Light Wipedown Interior', 'Tire Dressing', 'Hybrid Ceramic Spray']
}, {
  name: 'Full Detail',
  smMid: '$300',
  suvCross: '$350',
  note: 'Our most popular package — complete interior and exterior restoration',
  includes: ['Includes Interior Detail', 'Exterior Wash', 'Clay Bar Treatment', 'Clean Rims', 'Dress Tires', 'Hybrid Ceramic Spray', 'Deep Vacuum']
}, {
  name: 'Interior Detail',
  smMid: '$180',
  suvCross: '$195',
  note: 'Deep clean, shampoo, and steam sanitize every surface inside your vehicle',
  includes: ['Shampoo Seats', 'Deep Clean Interior', 'Vacuum', 'Shampoo Floor Mats', 'Steam & Sanitize']
}];
export const EXTRAS_TABLE = [{
  name: 'Engine Bay Top',
  price: '$120'
}, {
  name: 'Headlight Restore',
  price: '$80'
}, {
  name: 'Clay Bar Treatment',
  price: '$60'
}, {
  name: 'Water Spots / Dog Hair',
  price: 'Call for Pricing'
}, {
  name: 'Hybrid Ceramic Glass Treatment',
  price: '$200'
}, {
  name: 'Hybrid Ceramic Treatment — 1 Year',
  price: 'Call for Pricing'
}];
/** Cities with a dedicated service-area page. */
export const CITY_PAGE_AREAS = ['Camarillo', 'Ventura', 'Thousand Oaks', 'Simi Valley', 'Moorpark', 'Newbury Park', 'Westlake Village', 'Oak Park', 'Agoura Hills'];
/** Every community named as a service area on the website. */
export const ALL_SERVICE_AREAS = ['Camarillo', 'Ventura', 'Thousand Oaks', 'Simi Valley', 'Moorpark', 'Newbury Park', 'Westlake Village', 'Oak Park', 'Agoura Hills'];
/* ------------------------------------------------------------------ */
/* FAQ CONTENT, BY CATEGORY                                            */
/* ------------------------------------------------------------------ */
export const FAQ_CATEGORIES: FaqCategory[] = [{
  id: 'about-the-service',
  title: 'About Our Mobile Detailing Service',
  intro: "Mike's Pro Detail is a fully mobile car detailing service. We travel to your home, office, or apartment anywhere in Ventura County and complete the entire detail on-site. We bring our own water and detailing equipment. We require access to a standard electrical outlet at the service location.",
  faqs: [{
    question: 'What is mobile car detailing?',
    answer: 'Mobile car detailing is a professional vehicle cleaning service that comes directly to your home or workplace. Detailers bring all the equipment needed to thoroughly clean and restore your car inside and out.',
    onHomepage: true
  }, {
    question: 'Do you come to my home?',
    answer: "Yes. Mike's Pro Detail is a fully mobile service — we come to your home, office, or apartment complex and detail your vehicle on-site. There is no shop to drive to and no drop-off.",
    onHomepage: false
  }, {
    question: 'Can you detail my car at my home or workplace?',
    answer: 'Yes. Mobile detailing services can be performed at homes, offices, and apartment complexes as long as there is enough space to safely clean the vehicle without blocking traffic or access areas.',
    onHomepage: true
  }, {
    question: 'Do I need to be present during the detail?',
    answer: 'No. As long as the vehicle is accessible, you do not need to be present during the service.',
    onHomepage: true
  }, {
    question: 'Do I need to provide water?',
    answer: 'No. We bring our own water and detailing equipment. We require access to a standard electrical outlet at the service location.',
    onHomepage: false
  }, {
    question: 'Do I need to provide electricity?',
    answer: 'Yes. We bring our own water and detailing equipment. We require access to a standard electrical outlet at the service location.',
    onHomepage: false
  }, {
    question: 'Do mobile detailers bring their own water and power?',
    answer: 'We bring our own water and detailing equipment. We require access to a standard electrical outlet at the service location.',
    onHomepage: true
  }, {
    question: 'What do you need from me on the day of the appointment?',
    answer: 'Three things: access to your vehicle, enough open space around it for us to work safely without blocking traffic or driveways, and access to a standard electrical outlet at the service location. We bring our own water and detailing equipment.',
    onHomepage: false
  }]
}, {
  id: 'pricing',
  title: 'Pricing',
  intro: 'Mobile detailing at Mike\u2019s Pro Detail ranges from $90 to $350 depending on the package and the size of your vehicle. Every package is priced in two tiers: SM/MID (sedans and smaller vehicles) and SUV/CROSS (SUVs and crossovers).',
  faqs: [{
    question: 'How much does mobile car detailing cost?',
    answer: 'Our packages range from $90 to $350. A Basic Wash is $90 for SM/MID vehicles and $105 for SUV/Crossovers. Wash & Wax is $160 / $180. An Interior Detail is $180 / $195. Our Full Detail is $300 / $350. Call or text (818) 381-9905 for an exact quote on your vehicle.',
    onHomepage: true
  }, {
    question: 'How much does a full car detail cost?',
    answer: 'A Full Detail is $300 for small and mid-size vehicles and $350 for SUVs and crossovers. It is our most popular package and includes both the complete interior detail and the exterior wash, clay bar treatment, wheel cleaning, tire dressing, and hybrid ceramic wax.',
    onHomepage: false
  }, {
    question: 'How much does an interior detail cost?',
    answer: 'An Interior Detail is $180 for small and mid-size vehicles and $195 for SUVs and crossovers. It includes seat shampooing, deep interior cleaning, vacuuming, floor mat shampooing, and steam sanitizing.',
    onHomepage: false
  }, {
    question: 'Does the price change based on my vehicle size?',
    answer: 'Yes. Every package has two price tiers: SM/MID for sedans and smaller vehicles, and SUV/CROSS for SUVs and crossovers. Heavily soiled vehicles may require additional time or deep cleaning services.',
    onHomepage: false
  }, {
    question: 'What add-on services do you offer and what do they cost?',
    answer: 'Engine Bay Top is $120, Headlight Restore is $80, Clay Bar Treatment is $60, and the Hybrid Ceramic Glass Treatment is $200. Water spot removal, dog hair removal, and the 1-year Hybrid Ceramic Treatment are priced by condition — call (818) 381-9905 for a quote.',
    onHomepage: false
  }, {
    question: 'Is there a minimum for a basic wash?',
    answer: 'Yes. Basic Washes have a 2 car minimum. This minimum applies to Basic Washes only — all other packages can be booked for a single vehicle.',
    onHomepage: false
  }, {
    question: 'Do you have any current discounts?',
    answer: 'Yes. We are currently offering $50 off the listed price of Full Details and $20 off Wash & Wax for a limited time. Call or text (818) 381-9905 to book at the discounted rate.',
    onHomepage: false
  }]
}, {
  id: 'whats-included',
  title: "What's Included in Each Service",
  intro: 'Every package is performed by hand on-site using premium, eco-friendly products.',
  faqs: [{
    question: 'What is included in a full car detail?',
    answer: 'A full car detail usually includes exterior hand washing, a clay bar treatment to remove deep contamination, wheel cleaning, interior vacuuming, carpet and seat shampooing, leather cleaning and conditioning if applicable, steam sanitizing, interior surface cleaning, window cleaning, and tire dressing.',
    onHomepage: true
  }, {
    question: 'What is included in interior car detailing?',
    answer: 'Interior detailing typically includes vacuuming, wiping interior surfaces, cleaning door panels and dashboards, window cleaning, floor mat cleaning, seat shampooing or leather cleaning and conditioning, and steam sanitizing.',
    onHomepage: true
  }, {
    question: 'What is included in exterior car detailing?',
    answer: 'Exterior detailing includes hand washing, wheel and tire cleaning, bug removal, and paint protection treatments such as wax or sealant.',
    onHomepage: true
  }, {
    question: 'What is included in a Basic Wash?',
    answer: 'A Basic Wash includes an exterior wash, light interior wipe down, vacuum, rim cleaning, tire dressing, and window cleaning. Basic Washes have a 2 car minimum.',
    onHomepage: false
  }, {
    question: 'What is included in a Wash & Wax?',
    answer: 'A Wash & Wax includes an exterior wash, window cleaning, vacuum, light interior wipedown, tire dressing, and a hybrid ceramic wax. The wax adds a protective layer that helps your paint stay shiny and repel water and dirt.',
    onHomepage: false
  }, {
    question: 'What is engine bay detailing?',
    answer: 'Engine bay detailing safely removes grease, dust, and grime from your engine compartment. We protect sensitive electrical components, degrease, hand brush tight areas, rinse at low pressure, and finish with a non-greasy dressing on plastics and hoses. Engine Bay Top is $120 as an add-on.',
    onHomepage: false
  }, {
    question: 'How long does a full car detail take?',
    answer: 'Most full car detailing services take between 2–4 hours depending on the size and condition of the vehicle.',
    onHomepage: true
  }, {
    question: 'How long do your other services take?',
    answer: 'A wash and wax takes about 1.5–2 hours, interior detailing takes 2–3 hours, and a full detail takes 3–4 hours. We never rush — quality results take proper time.',
    onHomepage: false
  }]
}, {
  id: 'vehicles',
  title: 'Vehicles We Detail',
  intro: 'We detail cars, trucks, SUVs, vans, and classic vehicles. Pricing is set by vehicle size tier rather than by make or model.',
  faqs: [{
    question: 'What types of vehicles do you detail?',
    answer: 'We detail cars, trucks, SUVs, vans, and classic and collector vehicles. Pricing is based on two size tiers: SM/MID for sedans and smaller vehicles, and SUV/CROSS for SUVs and crossovers.',
    onHomepage: false
  }, {
    question: 'Can you detail trucks and SUVs?',
    answer: 'Yes. Mobile detailing services are available for cars, trucks, SUVs, and vans.',
    onHomepage: true
  }, {
    question: 'Do you clean heavily dirty vehicles?',
    answer: 'Yes. However, excessively dirty vehicles may require additional time or deep cleaning services depending on the level of buildup.',
    onHomepage: true
  }]
}, {
  id: 'capabilities',
  title: 'Stains, Pet Hair, Scratches & Restoration',
  intro: 'What our detailing can and cannot correct. We would rather set an accurate expectation than promise a result we cannot deliver.',
  faqs: [{
    question: 'Do you remove pet hair?',
    answer: 'Yes. Pet hair removal is part of our interior detailing work, using deep extraction vacuuming. Heavy dog hair is priced by condition — call (818) 381-9905 for a quote.',
    onHomepage: false
  }, {
    question: 'Can mobile detailing remove stains from seats and carpets?',
    answer: 'Yes. Professional detailing can remove many stains using steam cleaning, shampooing, and extraction methods. However, some stains may return depending on the type of spill and how long it has been present.',
    onHomepage: true
  }, {
    question: 'Can you remove odors from my car?',
    answer: 'Our shampooing and steam extraction lift most deep-set stains, and our sanitizing process neutralizes odors from spills, smoke, and pets.',
    onHomepage: false
  }, {
    question: 'Do you remove water spots?',
    answer: 'Yes. Water spots can often be improved or removed through paint polishing as part of a detail. Water spot removal is priced by condition — call (818) 381-9905 for a quote.',
    onHomepage: false
  }, {
    question: 'Can detailing remove light scratches from car paint?',
    answer: 'Light surface scratches and water spots can often be improved through paint polishing as part of a detail. Deeper scratches may require additional services.',
    onHomepage: true
  }, {
    question: 'Do you remove oxidation?',
    answer: 'Yes on headlights — our headlight restoration removes heavy oxidation from yellowed, cloudy lenses and finishes with a protective UV sealant. On paint, a clay bar treatment removes bonded contamination and polishing can improve light oxidation and dullness. For heavily oxidized paint, call (818) 381-9905 so we can assess the vehicle first.',
    onHomepage: false
  }, {
    question: 'Will exterior detailing remove swirl marks?',
    answer: 'A standard exterior detail cleans and protects but does not remove swirl marks. For added gloss and water beading, pair it with our hybrid ceramic spray protection.',
    onHomepage: false
  }, {
    question: 'Do you offer ceramic coating?',
    answer: 'We offer hybrid ceramic spray protection, which is not the same as a professional ceramic coating. Hybrid ceramic protection is included in our Wash & Wax and Full Detail packages. A Hybrid Ceramic Glass Treatment is $200, and a 1-year Hybrid Ceramic Treatment is priced by vehicle — call (818) 381-9905 for a quote.',
    onHomepage: false
  }, {
    question: "Does detailing help protect my car's paint?",
    answer: "Yes. Wax, sealants, and hybrid ceramic spray protection help shield your vehicle's paint from UV rays, dirt, and environmental contamination.",
    onHomepage: true
  }, {
    question: 'How often should I get my car detailed?',
    answer: 'Most vehicles benefit from professional detailing every 2–3 months to maintain appearance and protect the paint and interior.',
    onHomepage: true
  }]
}, {
  id: 'service-areas',
  title: 'Service Areas',
  intro: 'We serve Ventura County, California and select surrounding areas. We are a mobile business and do not have a storefront in any of these cities — we travel to you.',
  faqs: [{
    question: 'What areas do you service?',
    answer: 'Our service area is Ventura County, California and select surrounding areas — Camarillo, Ventura, Thousand Oaks, Simi Valley, Moorpark, Newbury Park, Westlake Village, Oak Park, and Agoura Hills. Call (818) 381-9905 to confirm your location.',
    onHomepage: false
  }, {
    question: 'Do you service my area?',
    answer: "Mobile detailing services are provided within our local service area across Ventura County and select surrounding areas. If you don't see your city listed, call (818) 381-9905 — we may still be able to come to you.",
    onHomepage: true
  }, {
    question: 'Do you have a shop I can drive to?',
    answer: 'No. We are a fully mobile detailing service with no storefront. Every detail is performed on-site at your home, office, or apartment.',
    onHomepage: false
  }]
}, {
  id: 'booking',
  title: 'Booking & Scheduling',
  intro: 'The fastest way to book is to call or text Mike directly at (818) 381-9905. You can also request a quote through the form on our website.',
  faqs: [{
    question: 'How do I schedule an appointment?',
    answer: 'Call or text Mike at (818) 381-9905, or fill out the quote form on our website with your vehicle and the service you need. We will confirm a price and find a time that works for you.',
    onHomepage: false
  }, {
    question: 'How do I book a mobile detailing appointment?',
    answer: 'You can schedule an appointment by calling or texting to choose a time that works best for you.',
    onHomepage: true
  }, {
    question: 'How far in advance should I schedule?',
    answer: 'Availability changes week to week, and we do offer flexible scheduling including weekends. Call or text (818) 381-9905 and we will tell you the next available appointment.',
    onHomepage: false
  }, {
    question: 'Do you work weekends?',
    answer: 'Yes. We offer flexible scheduling including weekends to fit around your work week.',
    onHomepage: false
  }, {
    question: 'Do you offer recurring or maintenance detailing?',
    answer: 'Yes. In addition to one-time details, we offer recurring maintenance plans that keep your vehicle in showroom condition year-round. Call (818) 381-9905 to set one up.',
    onHomepage: false
  }, {
    question: 'What happens if it rains on the day of my appointment?',
    answer: 'If weather conditions prevent the service, the appointment will be rescheduled for the next available day.',
    onHomepage: true
  }]
}, {
  id: 'payment',
  title: 'Payment',
  intro: 'Payment is collected after the detail is complete.',
  faqs: [{
    question: 'What payment methods do you accept?',
    answer: 'We accept cash, credit cards, and most digital payment methods.',
    onHomepage: true
  }]
}];
/** Every FAQ, flattened — used for the /faq page's FAQPage structured data. */
export const ALL_FAQS: Faq[] = FAQ_CATEGORIES.flatMap(c => c.faqs);
/** The shorter list rendered on the homepage, in the original order. */
export const homeFaqs: Faq[] = ALL_FAQS.filter(f => f.onHomepage);
